import type { ColorKind, ShapeKind } from '../assets/art/ids'
import type { LevelTray } from './colors-levels'

export const SNAP_PX = 32
export const DRAG_THRESHOLD_PX = 10
export const IDLE_HINT_MS = 8000
export const IDLE_HINT_MAX = 3
export const HINT_WIGGLE_MS = 900
export const HINT_REDUCED_MS = 600

export const CELEBRATE = {
  totalMs: 2800,
  hopAtMs: 150,
  hopDurationMs: 260,
  hopStaggerMs: 90,
  voiceAtMs: 300,
  confettiStartMs: 0,
  confettiEndMs: 1600,
  fadeAtMs: 2200,
  fadeMs: 300,
  nextAtMs: 2500,
  enterMs: 300,
  enterStaggerMs: 40,
  reducedHoldMs: 1600,
} as const

export const PLACED_SIZE_RATIO = 0.6
export const PLACED_PAIR_GAP_PX = 12
export const PLACED_OVERLAP = 0.35
export const PLACED_OVERLAP_FOUR = 0.5
export const PLACED_FAN_DEG = [-4, 0, 4, 8] as const
export const PLACED_EDGE_PX = 16
export const PLACED_FAN_WIDTH_RATIO = 0.8
export const PLACED_FOUR_WIDTH_RATIO = 0.88

export const CONFETTI = {
  count: 24,
  sizeMin: 16,
  sizeMax: 24,
  coneCenterDeg: 135,
  coneWidthDeg: 120,
  speedMin: 1000,
  speedMax: 1700,
  gravity: 1600,
  restitution: 0.55,
  inset: 24,
  fadeMs: 300,
  spinMin: -10,
  spinMax: 10,
} as const

const CONFETTI_KINDS = ['circle', 'square', 'triangle'] as const

export type DropResult = 'match' | 'mismatch'

export type PieceBox = { left: number; right: number; top: number; bottom: number }

export function pieceMatchesTray(
  piece: { shape: ShapeKind; color: ColorKind },
  tray: LevelTray,
): boolean {
  switch (tray.kind) {
    case 'shape':
      return piece.shape === tray.shape
    case 'color':
      return piece.color === tray.color
    default: {
      const _never: never = tray
      return _never
    }
  }
}

export function dropOnTray(
  piece: { shape: ShapeKind; color: ColorKind },
  tray: LevelTray,
): DropResult {
  return pieceMatchesTray(piece, tray) ? 'match' : 'mismatch'
}

export function isLevelComplete(placedCount: number, totalPieces: number): boolean {
  return totalPieces > 0 && placedCount >= totalPieces
}

export function boxesOverlap(a: PieceBox, b: PieceBox, extra: number): boolean {
  return !(
    a.right + extra < b.left ||
    a.left - extra > b.right ||
    a.bottom + extra < b.top ||
    a.top - extra > b.bottom
  )
}

function overlapArea(a: PieceBox, b: PieceBox, extra: number): number {
  const left = Math.max(a.left - extra, b.left)
  const right = Math.min(a.right + extra, b.right)
  const top = Math.max(a.top - extra, b.top)
  const bottom = Math.min(a.bottom + extra, b.bottom)
  const w = right - left
  const h = bottom - top
  if (w <= 0 || h <= 0) return 0
  return w * h
}

export function hitTrayIndex(
  pieceBox: PieceBox,
  trays: ReadonlyArray<PieceBox>,
  extra: number = SNAP_PX,
): number | null {
  let best: number | null = null
  let bestArea = 0
  for (let i = 0; i < trays.length; i++) {
    const tray = trays[i]
    if (!tray) continue
    const area = overlapArea(pieceBox, tray, extra)
    if (area > bestArea) {
      bestArea = area
      best = i
    }
  }
  return best
}

export type HintCandidate = {
  id: string
  placed: boolean
  x: number
  y: number
  trayX: number
  trayY: number
}

export function pickHintPiece(pieces: readonly HintCandidate[]): string | null {
  let best: HintCandidate | null = null
  let bestDist = Infinity
  for (const piece of pieces) {
    if (piece.placed) continue
    const dx = piece.x - piece.trayX
    const dy = piece.y - piece.trayY
    const dist = dx * dx + dy * dy
    if (dist < bestDist) {
      bestDist = dist
      best = piece
    }
  }
  return best?.id ?? null
}

export type PlacedLayout = {
  left: number
  top: number
  size: number
  rotateDeg: number
}

function fanUsableWidth(trayW: number, ratio: number): number {
  return Math.max(0, Math.min(trayW * ratio, trayW - PLACED_EDGE_PX * 2))
}

function placedRow(
  count: number,
  size: number,
  step: number,
  trayW: number,
  trayH: number,
  rotate: boolean,
): PlacedLayout[] {
  const totalW = size + step * (count - 1)
  const startX = (trayW - totalW) / 2
  const top = (trayH - size) / 2
  return Array.from({ length: count }, (_, index) => ({
    left: startX + index * step,
    top,
    size,
    rotateDeg: rotate ? (PLACED_FAN_DEG[index] ?? 8) : 0,
  }))
}

export function layoutPlacedPieces(
  count: number,
  trayW: number,
  trayH: number,
): PlacedLayout[] {
  if (count <= 0) return []
  if (count === 1) {
    const size = trayH * PLACED_SIZE_RATIO
    return placedRow(1, size, 0, trayW, trayH, false)
  }
  if (count === 2) {
    const size = trayH * PLACED_SIZE_RATIO
    return placedRow(2, size, size + PLACED_PAIR_GAP_PX, trayW, trayH, false)
  }
  const overlap = count === 4 ? PLACED_OVERLAP_FOUR : PLACED_OVERLAP
  const maxW = fanUsableWidth(
    trayW,
    count === 4 ? PLACED_FOUR_WIDTH_RATIO : PLACED_FAN_WIDTH_RATIO,
  )
  const size = Math.min(trayH * PLACED_SIZE_RATIO, maxW / (1 + (count - 1) * (1 - overlap)))
  return placedRow(count, size, size * (1 - overlap), trayW, trayH, true)
}

export type ConfettiParticle = {
  x: number
  y: number
  vx: number
  vy: number
  rot: number
  vr: number
  size: number
  kind: (typeof CONFETTI_KINDS)[number]
  color: string
}

export type ConfettiOrigin = { x: number; y: number }
export type ConfettiBounds = { width: number; height: number }

export function createConfettiParticle(
  origin: ConfettiOrigin,
  colors: readonly string[],
  index: number,
  rand: () => number = Math.random,
): ConfettiParticle {
  const kind = CONFETTI_KINDS[index % CONFETTI_KINDS.length] ?? 'circle'
  const color = colors[index % colors.length] ?? '#C4452D'
  const half = (CONFETTI.coneWidthDeg / 2) * (Math.PI / 180)
  const center = CONFETTI.coneCenterDeg * (Math.PI / 180)
  const angle = center + (rand() * 2 - 1) * half
  const speed = CONFETTI.speedMin + rand() * (CONFETTI.speedMax - CONFETTI.speedMin)
  return {
    x: origin.x,
    y: origin.y,
    vx: Math.cos(angle) * speed,
    vy: -Math.sin(angle) * speed,
    rot: rand() * Math.PI * 2,
    vr: CONFETTI.spinMin + rand() * (CONFETTI.spinMax - CONFETTI.spinMin),
    size: CONFETTI.sizeMin + rand() * (CONFETTI.sizeMax - CONFETTI.sizeMin),
    kind,
    color,
  }
}

export function stepConfettiParticle(
  particle: ConfettiParticle,
  dt: number,
  bounds: ConfettiBounds,
  inset: number = CONFETTI.inset,
): void {
  particle.vy += CONFETTI.gravity * dt
  particle.x += particle.vx * dt
  particle.y += particle.vy * dt
  particle.rot += particle.vr * dt
  const minX = inset
  const maxX = bounds.width - inset
  const minY = inset
  const maxY = bounds.height - inset
  if (particle.x < minX) {
    particle.x = minX
    particle.vx = Math.abs(particle.vx) * CONFETTI.restitution
  } else if (particle.x > maxX) {
    particle.x = maxX
    particle.vx = -Math.abs(particle.vx) * CONFETTI.restitution
  }
  if (particle.y < minY) {
    particle.y = minY
    particle.vy = Math.abs(particle.vy) * CONFETTI.restitution
  } else if (particle.y > maxY) {
    particle.y = maxY
    particle.vy = -Math.abs(particle.vy) * CONFETTI.restitution
  }
}

export function confettiOpacity(
  elapsedMs: number,
  durationMs: number,
  fadeMs: number = CONFETTI.fadeMs,
): number {
  if (elapsedMs >= durationMs) return 0
  const fadeStart = durationMs - fadeMs
  if (elapsedMs <= fadeStart) return 1
  return Math.max(0, 1 - (elapsedMs - fadeStart) / fadeMs)
}
